"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getLogSqrtPriceFloor = exports.getSqrtPrice = exports.TWO = exports.ONE = exports.ZERO = exports.MaxUint256 = void 0;
const bignumber_1 = require("@ethersproject/bignumber");
const jsbi_1 = __importDefault(require("jsbi"));
const tiny_invariant_1 = __importDefault(require("tiny-invariant"));
const Q32 = jsbi_1.default.exponentiate(jsbi_1.default.BigInt(2), jsbi_1.default.BigInt(32));
const MIN_POINT = -887272;
const MAX_POINT = -MIN_POINT;
const MIN_SQRT_RATIO = jsbi_1.default.BigInt('4295128739');
const MAX_SQRT_RATIO = jsbi_1.default.BigInt('1461446703485210103287273052203988822378723970342');
exports.MaxUint256 = jsbi_1.default.BigInt('0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff');
exports.ZERO = jsbi_1.default.BigInt(0);
exports.ONE = jsbi_1.default.BigInt(1);
exports.TWO = jsbi_1.default.BigInt(2);
const POWERS_OF_2 = [128, 64, 32, 16, 8, 4, 2, 1].map((pow) => [
    pow,
    jsbi_1.default.exponentiate(exports.TWO, jsbi_1.default.BigInt(pow)),
]);
function mulShift(val, mulBy) {
    return jsbi_1.default.signedRightShift(jsbi_1.default.multiply(val, jsbi_1.default.BigInt(mulBy)), jsbi_1.default.BigInt(128));
}
function mostSignificantBit(x) {
    let msb = 0;
    for (const [power, min] of POWERS_OF_2) {
        if (jsbi_1.default.greaterThanOrEqual(x, min)) {
            x = jsbi_1.default.signedRightShift(x, jsbi_1.default.BigInt(power));
            msb += power;
        }
    }
    return msb;
}
function getSqrtPrice(point) {
    (0, tiny_invariant_1.default)(point >= MIN_POINT && point <= MAX_POINT && Number.isInteger(point), 'PointOverRange');
    const absTick = point < 0 ? point * -1 : point;
    let ratio = (absTick & 0x1) !== 0
        ? jsbi_1.default.BigInt('0xfffcb933bd6fad37aa2d162d1a594001')
        : jsbi_1.default.BigInt('0x100000000000000000000000000000000');
    if ((absTick & 0x2) !== 0)
        ratio = mulShift(ratio, '0xfff97272373d413259a46990580e213a');
    if ((absTick & 0x4) !== 0)
        ratio = mulShift(ratio, '0xfff2e50f5f656932ef12357cf3c7fdcc');
    if ((absTick & 0x8) !== 0)
        ratio = mulShift(ratio, '0xffe5caca7e10e4e61c3624eaa0941cd0');
    if ((absTick & 0x10) !== 0)
        ratio = mulShift(ratio, '0xffcb9843d60f6159c9db58835c926644');
    if ((absTick & 0x20) !== 0)
        ratio = mulShift(ratio, '0xff973b41fa98c081472e6896dfb254c0');
    if ((absTick & 0x40) !== 0)
        ratio = mulShift(ratio, '0xff2ea16466c96a3843ec78b326b52861');
    if ((absTick & 0x80) !== 0)
        ratio = mulShift(ratio, '0xfe5dee046a99a2a811c461f1969c3053');
    if ((absTick & 0x100) !== 0)
        ratio = mulShift(ratio, '0xfcbe86c7900a88aedcffc83b479aa3a4');
    if ((absTick & 0x200) !== 0)
        ratio = mulShift(ratio, '0xf987a7253ac413176f2b074cf7815e54');
    if ((absTick & 0x400) !== 0)
        ratio = mulShift(ratio, '0xf3392b0822b70005940c7a398e4b70f3');
    if ((absTick & 0x800) !== 0)
        ratio = mulShift(ratio, '0xe7159475a2c29b7443b29c7fa6e889d9');
    if ((absTick & 0x1000) !== 0)
        ratio = mulShift(ratio, '0xd097f3bdfd2022b8845ad8f792aa5825');
    if ((absTick & 0x2000) !== 0)
        ratio = mulShift(ratio, '0xa9f746462d870fdf8a65dc1f90e061e5');
    if ((absTick & 0x4000) !== 0)
        ratio = mulShift(ratio, '0x70d869a156d2a1b890bb3df62baf32f7');
    if ((absTick & 0x8000) !== 0)
        ratio = mulShift(ratio, '0x31be135f97d08fd981231505542fcfa6');
    if ((absTick & 0x10000) !== 0)
        ratio = mulShift(ratio, '0x9aa508b5b7a84e1c677de54f3e99bc9');
    if ((absTick & 0x20000) !== 0)
        ratio = mulShift(ratio, '0x5d6af8dedb81196699c329225ee604');
    if ((absTick & 0x40000) !== 0)
        ratio = mulShift(ratio, '0x2216e584f5fa1ea926041bedfe98');
    if ((absTick & 0x80000) !== 0)
        ratio = mulShift(ratio, '0x48a170391f7dc42444e8fa2');
    if (point > 0)
        ratio = jsbi_1.default.divide(exports.MaxUint256, ratio);
    // back to Q96
    return jsbi_1.default.greaterThan(jsbi_1.default.remainder(ratio, Q32), exports.ZERO)
        ? bignumber_1.BigNumber.from(jsbi_1.default.add(jsbi_1.default.divide(ratio, Q32), exports.ONE).toString())
        : bignumber_1.BigNumber.from(jsbi_1.default.divide(ratio, Q32).toString());
}
exports.getSqrtPrice = getSqrtPrice;
function getLogSqrtPriceFloor(_sqrtRatioX96) {
    const sqrtRatioX96 = jsbi_1.default.BigInt(_sqrtRatioX96.toString());
    (0, tiny_invariant_1.default)(jsbi_1.default.greaterThanOrEqual(sqrtRatioX96, MIN_SQRT_RATIO)
        && jsbi_1.default.lessThan(sqrtRatioX96, MAX_SQRT_RATIO), 'SqrtPriceOverRange');
    const sqrtRatioX128 = jsbi_1.default.leftShift(sqrtRatioX96, jsbi_1.default.BigInt(32));
    const msb = mostSignificantBit(sqrtRatioX128);
    let r;
    if (jsbi_1.default.greaterThanOrEqual(jsbi_1.default.BigInt(msb), jsbi_1.default.BigInt(128)))
        r = jsbi_1.default.signedRightShift(sqrtRatioX128, jsbi_1.default.BigInt(msb - 127));
    else
        r = jsbi_1.default.leftShift(sqrtRatioX128, jsbi_1.default.BigInt(127 - msb));
    let log_2 = jsbi_1.default.leftShift(jsbi_1.default.subtract(jsbi_1.default.BigInt(msb), jsbi_1.default.BigInt(128)), jsbi_1.default.BigInt(64));
    for (let i = 0; i < 14; i++) {
        r = jsbi_1.default.signedRightShift(jsbi_1.default.multiply(r, r), jsbi_1.default.BigInt(127));
        const f = jsbi_1.default.signedRightShift(r, jsbi_1.default.BigInt(128));
        log_2 = jsbi_1.default.bitwiseOr(log_2, jsbi_1.default.leftShift(f, jsbi_1.default.BigInt(63 - i)));
        r = jsbi_1.default.signedRightShift(r, f);
    }
    const log_sqrt10001 = jsbi_1.default.multiply(log_2, jsbi_1.default.BigInt('255738958999603826347141'));
    const pointLow = jsbi_1.default.toNumber(jsbi_1.default.signedRightShift(jsbi_1.default.subtract(log_sqrt10001, jsbi_1.default.BigInt('3402992956809132418596140100660247210')), jsbi_1.default.BigInt(128)));
    const pointHigh = jsbi_1.default.toNumber(jsbi_1.default.signedRightShift(jsbi_1.default.add(log_sqrt10001, jsbi_1.default.BigInt('291339464771989622907027621153398088495')), jsbi_1.default.BigInt(128)));
    return pointLow === pointHigh
        ? pointLow
        : jsbi_1.default.lessThanOrEqual(jsbi_1.default.BigInt(getSqrtPrice(pointHigh).toString()), sqrtRatioX96)
            ? pointHigh
            : pointLow;
}
exports.getLogSqrtPriceFloor = getLogSqrtPriceFloor;
